import React, { useEffect, useState } from 'react'
import useGetAccountGroup from '../../account-management/account-list/hooks/useGetAccountGroup'
import { useModal } from '../../../context/ModalProvider'
import { CloseButton, ETitle, Modal } from '../../../components/ui'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { bookCollectionService } from '../../../service/BookCollectionService'
import {
  showErrorToast,
  showSuccessToast,
  toastKey,
} from '../../../utils/showToast'
import { useQueryClient } from '@tanstack/react-query'
import { bookPackageKeys } from '../../../data/queryFactories'
import useGetBookCollection from '../book-list/hooks/useGetBookCollection'
const bookPackageSchema = z.object({
  groups: z
    .array(z.string())
    .min(1, { message: 'Gói sách phải thuộc ít nhất một nhóm' }),
})

export default function BookPackageDecentralizeModal({
  bookPackageName = '',
  uuid,
}) {
  const { handleCloseModal } = useModal()
  const { data, isLoading, error } = useGetAccountGroup()

  //Todo: When the book collection api get updated, update the selectedGroups for init values
  const { data: { collections = [] } = {} } = useGetBookCollection()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedGroups, setSelectedGroups] = useState([])
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(bookPackageSchema),
    defaultValues: {
      groups: [],
    },
  })
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const info = await bookCollectionService.getInfoUserOfBookPackage(uuid)
        if (info) {
          setSelectedGroups(info.groups.map((group) => group.uuid))
        }
      } catch (err) {
        throw new Error(
          err.message || 'Lỗi khi lấy dữ liệu nhóm người dùng của gói sách'
        )
      }
    }
    fetchInfo()
  }, [])
  useEffect(() => {
    if (!isLoading && (error || !data)) {
      handleCloseModal()
      return
    }
  }, [isLoading])

  // Update form value when selectedGroups changes
  useEffect(() => {
    setValue('groups', selectedGroups)
  }, [selectedGroups, setValue])

  // Filter groups based on search term
  const filteredGroups =
    data?.groups?.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || []

  const handleCheckboxChange = (uuid) => {
    setSelectedGroups((prev) => {
      if (prev.includes(uuid)) {
        return prev.filter((id) => id !== uuid)
      } else {
        return [...prev, uuid]
      }
    })
  }

  const onSubmit = async (formData) => {
    try {
      const response = await bookCollectionService.addGroups({
        collection: uuid,
        groups: formData.groups,
      })
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: bookPackageKeys.all })
        showSuccessToast(
          'Cập nhật quyền truy cập thành công',
          toastKey.BOOKS_MANAGEMENT.PACKAGE.LIST.GET
        )
        handleCloseModal()
      } else {
        showErrorToast({
          message: response.message?.content || 'Có lỗi xảy ra',
          toastId: toastKey.BOOKS_MANAGEMENT.PACKAGE.LIST.GET,
        })
      }
    } catch (error) {
      showErrorToast({
        message: 'Có lỗi xảy ra, vui lòng thử lại',
        toastId: toastKey.BOOKS_MANAGEMENT.PACKAGE.LIST.GET,
      })
    }
  }
  return (
    <Modal
      onClose={() => {
        handleCloseModal()
      }}
      className="w-[500px] rounded-md bg-white p-8 text-gray-accent"
    >
      <header className="relative w-full">
        <ETitle text={'Quản lý quyền truy cập'} />
        <p className="mt-2 text-center text-gray-500">
          &lt;&lt;{bookPackageName}&gt;&gt;
        </p>
        <CloseButton
          className="absolute right-0 top-0"
          onClickHandler={() => {
            handleCloseModal()
          }}
        />
      </header>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        className="mt-4 space-y-4"
      >
        <div className="rounded-md border">
          <div className="border-b p-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Tìm kiếm..."
                className="w-full rounded-md border py-2 pl-10 pr-4"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 transform">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto p-4">
            {filteredGroups.map((group) => (
              <div key={group.uuid} className="mb-3 flex items-center">
                <div className="relative mr-3 h-5 w-5">
                  <input
                    type="checkbox"
                    id={group.uuid}
                    value={group.uuid}
                    checked={selectedGroups.includes(group.uuid)}
                    onChange={() => handleCheckboxChange(group.uuid)}
                    className="peer absolute h-5 w-5 cursor-pointer opacity-0"
                  />
                  <div className="pointer-events-none h-5 w-5 rounded border border-gray-300 bg-white peer-checked:bg-gray-accent">
                    {selectedGroups.includes(group.uuid) && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-full w-full text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <label
                  htmlFor={group.uuid}
                  className="cursor-pointer text-gray-700"
                >
                  {group.name}
                </label>
              </div>
            ))}
            {filteredGroups.length === 0 && (
              <p className="py-2 text-center text-gray-500">
                Không tìm thấy kết quả
              </p>
            )}
          </div>
        </div>

        {errors.groups && (
          <p className="text-sm text-red-500">{errors.groups.message}</p>
        )}

        <div className="mt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCloseModal}
            className="rounded-md border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50"
          >
            Hủy
          </button>
          <button
            type="submit"
            className="rounded-md bg-gray-800 px-6 py-2 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            Xác nhận
          </button>
        </div>
      </form>
    </Modal>
  )
}
